import React from "react";
import { Box, Typography } from "@mui/material";
import type { LifecycleRetreat } from "../../../types/retreatLifecycle";
import { useManageRetreat } from "./manage/useManageRetreat";
import { ManageRetreatHeader } from "./manage/ManageRetreatHeader";
import { ManageRetreatKpis } from "./manage/ManageRetreatKpis";
import { ManageRetreatModals } from "./manage/ManageRetreatModals";
import { ProjectedSection } from "./manage/sections/ProjectedSection";
import { InSetupSection } from "./manage/sections/InSetupSection";
import { RegistrationOpenSection } from "./manage/sections/RegistrationOpenSection";
import { ClosingSection } from "./manage/sections/ClosingSection";
import { ClosedSection } from "./manage/sections/ClosedSection";
import { DeletedSection } from "./manage/sections/DeletedSection";

const ManageRetreat = () => {
  const m = useManageRetreat();

  const showRosterSelect =
    !m.isSuperAdmin &&
    (m.userData?.access === true || m.userData?.committeeMember === true);

  const actionHandlers = {
    onHistory: m.openHistory,
    onDelete: m.askDelete,
    onApprove: m.approveFromTable,
    onRestore: (r: LifecycleRetreat) => m.setRestoreTarget(r),
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1640,
        mx: "auto",
        pb: 6,
        minWidth: 0,
        px: { xs: 0.5, sm: 0 },
        overflowX: "hidden",
      }}
    >
      <ManageRetreatHeader
        isSuperAdmin={m.isSuperAdmin}
        showRosterSelect={Boolean(showRosterSelect)}
        accessedRetreats={m.accessedRetreats}
        onOpenPasswordModal={m.openPasswordModal}
      />

      <ManageRetreatKpis
        isSuperAdmin={m.isSuperAdmin}
        counts={m.counts}
        onScrollTo={m.scrollTo}
      />

      <Typography
        variant="h5"
        sx={{
          mt: 2,
          mb: 1.5,
          fontFamily: 'Inter, "Segoe UI", Roboto, Arial, sans-serif',
          fontWeight: 750,
          color: "#213449",
          fontSize: { xs: "1.15rem", sm: "1.35rem" },
          letterSpacing: "-0.02em",
          px: { xs: 0.5, sm: 0 },
        }}
      >
        Retreat Management Pipeline
      </Typography>

      {m.isSuperAdmin && (
        <ProjectedSection
          rows={m.rows.projected}
          total={m.totals.projected}
          loading={m.loadingSection.projected}
          searchValue={m.search.projected}
          onSearchChange={(value) => m.onSearchChange("projected", value)}
          parishes={m.parishes}
          projectedParishFilter={m.projectedParishFilter}
          projectedTypeFilter={m.projectedTypeFilter}
          onParishFilter={(value) => {
            m.setProjectedParishFilter(value);
            m.projectedParishFilterRef.current = value;
            m.loadSection("projected", 1, 10, {}, { parishValue: value || null });
          }}
          onTypeFilter={(type) => {
            m.setProjectedTypeFilter(type);
            m.projectedTypeFilterRef.current = type;
            m.loadSection("projected", 1, 10, {}, { typeFilter: type || null });
          }}
          onClearFilters={() => {
            m.setProjectedParishFilter("");
            m.projectedParishFilterRef.current = "";
            m.setProjectedTypeFilter("");
            m.projectedTypeFilterRef.current = "";
            m.loadSection("projected", 1, 10, {}, {
              parishValue: null,
              typeFilter: null,
            });
          }}
          dataFetchApi={m.fetchFor.projected}
          isSuperAdmin={m.isSuperAdmin}
          {...actionHandlers}
        />
      )}

      <InSetupSection
        rows={m.rows.inSetup}
        total={m.totals.inSetup}
        loading={m.loadingSection.inSetup}
        searchValue={m.search.inSetup}
        onSearchChange={(value) => m.onSearchChange("inSetup", value)}
        dataFetchApi={m.fetchFor.inSetup}
        isSuperAdmin={m.isSuperAdmin}
        onSetupMail={m.openSetupMail}
        {...actionHandlers}
      />

      <RegistrationOpenSection
        rows={m.rows.registrationOpen}
        total={m.totals.registrationOpen}
        loading={m.loadingSection.registrationOpen}
        searchValue={m.search.registrationOpen}
        onSearchChange={(value) => m.onSearchChange("registrationOpen", value)}
        parishes={m.parishes}
        parishFilter={m.parishFilter}
        onParishFilter={(value) => {
          m.setParishFilter(value);
          m.parishFilterRef.current = value;
          m.loadSection("registrationOpen", 1, 10, {}, {
            parishValue: value || null,
          });
        }}
        onClearParish={() => {
          m.setParishFilter("");
          m.parishFilterRef.current = "";
          m.loadSection("registrationOpen", 1, 10, {}, { parishValue: null });
        }}
        dataFetchApi={m.fetchFor.registrationOpen}
        columns={m.openColumns}
        isSuperAdmin={m.isSuperAdmin}
        canEdit={m.canEdit}
        onSelectedRowsChange={(state: any) => {
          const { selectedRows, allSelected: selectedAll } = state;
          m.setAllSelected(Boolean(selectedAll));
          m.setSelectedRetreats(
            (selectedRows || []).map((row: LifecycleRetreat) => row.id),
          );
        }}
        onHistory={m.openHistory}
        onDelete={m.askDelete}
        onStatusToggle={m.handleShowStatus}
        onEditInactiveReason={m.handleEditInactiveReason}
        onWaitlistExport={m.exportWaitlist}
        onFreezeToggle={m.handleShowFreeze}
        exporting={m.exporting}
        onExport={m.exportRetreatData}
      />

      <ClosingSection
        rows={m.rows.closing}
        total={m.totals.closing}
        loading={m.loadingSection.closing}
        searchValue={m.search.closing}
        onSearchChange={(value) => m.onSearchChange("closing", value)}
        dataFetchApi={m.fetchFor.closing}
        isSuperAdmin={m.isSuperAdmin}
        actor={m.actor}
        retreatApi={m.retreatApi}
        reloadAll={m.reloadAll}
        onHistory={m.openHistory}
      />

      <ClosedSection
        rows={m.rows.completed}
        total={m.totals.completed}
        loading={m.loadingSection.completed}
        searchValue={m.search.completed}
        onSearchChange={(value) => m.onSearchChange("completed", value)}
        dataFetchApi={m.fetchFor.completed}
        isSuperAdmin={m.isSuperAdmin}
        {...actionHandlers}
      />

      <DeletedSection
        rows={m.rows.deleted}
        total={m.totals.deleted}
        loading={m.loadingSection.deleted}
        searchValue={m.search.deleted}
        onSearchChange={(value) => m.onSearchChange("deleted", value)}
        dataFetchApi={m.fetchFor.deleted}
        isSuperAdmin={m.isSuperAdmin}
        {...actionHandlers}
      />

      <ManageRetreatModals {...m} />
    </Box>
  );
};

export default ManageRetreat;
